import { SortableHeadCell, TextCell } from 'components/tables/cells';
import { createColumnHelper } from '@tanstack/table-core';
import { TextColumnProps } from 'components/tables/columnTypes';
import { AdminAccount } from 'domain/account/interfaces/AdminAccount';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import { sortByDate } from 'components/tables/sorting/sortByDate';
import { getAdminRoleNameFromValue } from 'domain/account/enums';

const columnHelper = createColumnHelper<AdminAccount>();

export const adminAccountTableColumns = {
	fullName: (overrideProps?: TextColumnProps<AdminAccount>) =>
		columnHelper.accessor((row) => getAdminNameOrNotAvailable(row), {
			id: 'fullName',
			enableSorting: true,
			size: 100,
			header: ({ header }) => (
				<SortableHeadCell
					text="Name"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	email: (overrideProps?: TextColumnProps<AdminAccount>) =>
		columnHelper.accessor((row) => row.email, {
			id: 'email',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Email"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	roles: (overrideProps?: TextColumnProps<AdminAccount>) =>
		columnHelper.accessor(
			(row) =>
				row.roles
					.map((role) => getAdminRoleNameFromValue(role))
					.join(', '),
			{
				id: 'roles',
				enableSorting: true,
				header: ({ header }) => (
					<SortableHeadCell
						text="Roles"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						withTooltip
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	registered: (overrideProps?: TextColumnProps<AdminAccount>) =>
		columnHelper.accessor(
			(row) => toDefaultDateFormatOrNotAvailable(row.registered),
			{
				id: 'registered',
				enableSorting: true,
				sortingFn: (a, b) =>
					sortByDate(a.original.registered, b.original.registered),
				header: ({ header }) => (
					<SortableHeadCell
						text="Registered"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
};

export const getAdminNameOrNotAvailable = (admin: AdminAccount) => {
	if (!admin?.firstName && !admin?.lastName) {
		return `N/A`;
	}
	return `${admin.firstName ?? ''} ${admin.lastName ?? ''}`;
};
